import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { FileText, Upload, AlertCircle, CheckCircle, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

interface ContractAnalysis {
  exclusivity: {
    present: boolean;
    duration: string;
    scope: string;
    explanation: string;
  };
  termination: {
    present: boolean;
    noticePeriod: string;
    causeRequired: boolean;
    explanation: string;
  };
  usageRights: {
    duration: string;
    scope: string;
    perpetual: boolean;
    explanation: string;
  };
  paymentTerms: {
    structure: string;
    milestones: string[];
    explanation: string;
  };
  redFlags: Array<{
    issue: string;
    severity: string;
    explanation: string;
  }>;
  riskScore: "Low" | "Medium" | "High";
  summary: string;
}

export function ContractAnalyzer({ dealId }: { dealId?: string }) {
  const [file, setFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<ContractAnalysis | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setAnalysis(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error("Please upload a contract file first");
      return;
    }

    setIsAnalyzing(true);
    try {
      // Read file as text
      const text = await file.text();

      // Call analysis edge function
      const { data, error } = await supabase.functions.invoke("analyze-contract", {
        body: { contractText: text, dealId },
      });

      if (error) throw error;

      setAnalysis(data.analysis);
      toast.success("Contract analyzed successfully");
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze contract", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getRiskBadgeVariant = (risk: string) => {
    switch (risk) {
      case "Low":
        return "default";
      case "Medium":
        return "secondary";
      case "High":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case "critical":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case "high":
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case "medium":
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      default:
        return <CheckCircle className="h-4 w-4 text-success" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>AI Contract Analysis</CardTitle>
          <CardDescription>
            Upload your NIL contract for automated legal term extraction and risk assessment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <input
              type="file"
              accept=".txt,.pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
              id="contract-upload"
            />
            <label htmlFor="contract-upload" className="cursor-pointer">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm font-medium mb-1">
                {file ? file.name : "Click to upload contract"}
              </p>
              <p className="text-xs text-muted-foreground">
                PDF, TXT, DOC, or DOCX (max 10MB)
              </p>
            </label>
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={!file || isAnalyzing}
            className="w-full"
          >
            <FileText className="mr-2 h-4 w-4" />
            {isAnalyzing ? "Analyzing..." : "Analyze Contract"}
          </Button>
        </CardContent>
      </Card>

      {analysis && (
        <>
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>Analysis Summary</CardTitle>
                  <CardDescription>AI-powered contract review</CardDescription>
                </div>
                <Badge variant={getRiskBadgeVariant(analysis.riskScore)}>
                  Risk: {analysis.riskScore}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{analysis.summary}</p>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Exclusivity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {analysis.exclusivity.present ? (
                      <AlertCircle className="h-4 w-4 text-warning" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-success" />
                    )}
                    <span className="text-sm font-medium">
                      {analysis.exclusivity.present ? "Present" : "Not Present"}
                    </span>
                  </div>
                  {analysis.exclusivity.present && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Duration: {analysis.exclusivity.duration}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Scope: {analysis.exclusivity.scope}
                      </p>
                    </>
                  )}
                  <p className="text-sm">{analysis.exclusivity.explanation}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Termination Rights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {analysis.termination.present ? (
                      <CheckCircle className="h-4 w-4 text-success" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                    <span className="text-sm font-medium">
                      {analysis.termination.present ? "Present" : "Not Present"}
                    </span>
                  </div>
                  {analysis.termination.present && (
                    <p className="text-sm text-muted-foreground">
                      Notice: {analysis.termination.noticePeriod}
                      {analysis.termination.causeRequired && " (Cause required)"}
                    </p>
                  )}
                  <p className="text-sm">{analysis.termination.explanation}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Usage Rights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {analysis.usageRights.perpetual ? (
                      <AlertCircle className="h-4 w-4 text-warning" />
                    ) : (
                      <CheckCircle className="h-4 w-4 text-success" />
                    )}
                    <span className="text-sm font-medium">
                      {analysis.usageRights.perpetual ? "Perpetual" : "Limited Term"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Duration: {analysis.usageRights.duration}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Scope: {analysis.usageRights.scope}
                  </p>
                  <p className="text-sm">{analysis.usageRights.explanation}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Payment Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm font-medium">{analysis.paymentTerms.structure}</p>
                  {analysis.paymentTerms.milestones.length > 0 && (
                    <ul className="text-sm text-muted-foreground list-disc list-inside">
                      {analysis.paymentTerms.milestones.map((milestone, i) => (
                        <li key={i}>{milestone}</li>
                      ))}
                    </ul>
                  )}
                  <p className="text-sm">{analysis.paymentTerms.explanation}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {analysis.redFlags.length > 0 && (
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  Red Flags Detected
                </CardTitle>
                <CardDescription>
                  Concerning terms that require attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analysis.redFlags.map((flag, index) => (
                    <div key={index} className="flex gap-3 p-3 border rounded-lg">
                      {getSeverityIcon(flag.severity)}
                      <div className="flex-1">
                        <div className="font-medium text-sm">{flag.issue}</div>
                        <div className="text-sm text-muted-foreground">{flag.explanation}</div>
                        <Badge variant="outline" className="mt-1">
                          {flag.severity}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}